<template>
  <div
    class="review-card"
    :class="{'_grey': grey}"
    itemscope
    itemtype="https://schema.org/Review"
  >
    <div class="review-card__inner">
      <div class="review-card__head">
        <div class="review-card__info">
          <div class="review-card__user">
            <div class="review-card__user-name">
              <span
                ref="userName"
                v-clamp="3"
                itemprop="author"
                itemscope
                itemtype="https://schema.org/Person"
              >
                <span itemprop="name">{{ review.name }}</span>
              </span>
            </div>
          </div>
          <StarRating
            :stars="review.rating"
            class="review-card__star-rating"
            :is-small="true"
            itemprop="reviewRating"
            itemscope
            itemtype="https://schema.org/Rating"
          >
            <meta itemprop="ratingValue" :content="String(review.rating)">
          </StarRating>
        </div>

        <div class="review-card__img">
          <img
            v-if="review.image"
            :src="review.image"
            :alt="'Изображение: '+review.name"
            itemprop="image"
          >
          <app-svg v-else id="user-hovered" width="50" height="50"></app-svg>
        </div>
      </div>

      <div class="review-card__body">
        <div
          v-clamp="7"
          class="review-card__text"
          v-html="review.message"
          itemprop="reviewBody"
        />
        <span
          class="review-card__read-more _read-more"
          @click="isOpenFullReview = true"
        >Читать полностью</span>
      </div>

      <div class="review-card__bottom">
        <div
          v-if="review.created_at"
          v-date
          class="review-card__info-date"
          itemprop="datePublished"
          :content="review.created_at"
        >
          {{ review.created_at }}
        </div>
      </div>
    </div>

    <teleport v-if="isOpenFullReview" to="body">
      <AppModal
        v-model="isOpenFullReview"
        class="review-card__full-review"
      >
        <div class="review-card__inner">
          <div class="review-card__left">
            <div class="review-card__head">
              <div class="review-card__user">
                <div class="review-card__user-name">
                  <span ref="userName" v-clamp="2">{{ review.name }}</span>
                </div>
              </div>
              <div class="review-card__info">
                <div
                  v-if="review.created_at"
                  v-date
                  class="review-card__info-date"
                >
                  {{ review.created_at }}
                </div>
                <StarRating
                  class="review-card__star-rating"
                  :is-small="true"
                  :stars="review.rating"
                />
              </div>
            </div>

            <div class="review-card__body">
              <div class="review-card__modal-text">
                {{ review.message }}
              </div>
            </div>
          </div>
          <div v-if="review.image" class="review-card__right">
            <img
              v-if="review.image"
              :src="review.image"
              :alt="'Изображение №2: '+review.name"
            >
          </div>
        </div>
      </AppModal>
    </teleport>
  </div>
</template>

<script>
import StarRating from "./ui/StarRating.vue";
import AppModal from "./ui/AppModal.vue";

export default {
  name: "ReviewsSectionCard",
  components: { StarRating, AppModal },

  props: {
    review: {
      type: Object,
      default: () => { },
    },
    grey: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      firstLetter: "",
      isOpenFullReview: false,
    };
  },

  mounted() {
    this.getFirstLetter();
  },

  methods: {
    getFirstLetter() {
      const userName = this.$refs.userName.innerHTML.trim();
      this.firstLetter = userName.substring(0, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.review-card {
  $that: &;

  min-height: 375px;
  height: 100%;
  background: $white;
  border-bottom-right-radius: 90px;
  padding: 42px 56px;

  &._grey {
    background: $color_dark-20;
  }

  @include desktop {
    min-height: 285px;
    padding: 30px 38px;
  }

  @include phone {
    border-bottom-right-radius: 30px;
    padding: 30px 38px;
  }

  &__full-review {

    #{$that} {
      &__inner {
        max-width: 1000px;
        display: flex;
        flex-direction: row;
        gap: 20px;
        padding: 68px 104px;
      }

      &__left, &__right {
        flex: 1;
      }

      &__right {
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 100%;
        }
      }

      &__head {
        gap: 20px;
        padding: 0;
      }

      &__info {
        flex-direction: column-reverse;
        align-items: flex-start;
        gap: 20px;
      }
    }
  }

  &__inner {
    max-width: 776px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    @include desktop {
      gap: 9px;
    }

    #{$that}__full-review & {
      flex-direction: column;
    }
  }

  &__user {
    display: flex;

    @include desktop {
      align-self: flex-start;
    }

    #{$that}__full-review & {
      align-self: flex-start;
    }
  }

  &__user-avatar {
    flex: none;
    margin-right: 16px;
    background-color: $color_grey-15;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    color: $white;
    font-size: 40px;
    line-height: 56px;

    @include flex-center;

    @include desktop {
      width: 40px;
      height: 40px;
      font-size: 26px;
      line-height: 36px;
      margin-right: 8px;
    }
  }

  &__user-name {
    font-size: 20px;
    line-height: 28px;
    color: $color_orange-50;

    @include flex-center;
  }

  &__info {
    flex: none;
    padding-right: 22px;
    min-height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 36px;

    @include desktop {
      padding-right: 0;
      align-items: center;
      min-height: auto;
      padding-bottom: 8px;
    }

    @include phone {
      min-width: auto;
      gap: 26px;
      padding-top: 8px;
      padding-bottom: 0;
    }

    #{$that}__full-review & {
      flex-direction: row-reverse;
      align-items: center;
      min-width: 100%;
      padding-right: 0;

      @include desktop {
        padding-top: 8px;
        padding-bottom: 0;
      }
    }
  }

  &__info-date {
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 18px;
    color: #0d0e17;
  }

  &__star-rating {}

  &__body {
    margin-top: 24px;
    text-align: left;
  }

  &__text {
    text-align: left;
    line-height: 24px;
    overflow: hidden;
    color: #0d0e17;

    display: -webkit-box;
    -webkit-line-clamp: 7;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;

    @include desktop {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__read-more {
    color: $color_orange-50;
    text-align: left;
    text-decoration: none;
    margin-top: 14px;
  }

  &__modal-text {
    text-align: left;
    line-height: 24px;
    overflow: hidden;

    @include desktop {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__bottom {
    margin-top: auto;
  }

  &__img {
    max-width: 100px;
    max-height: 100px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @include phone {
      max-width: 80px;
      max-height: 80px;

      svg {
        width: 30px;
        height: 30px;
      }
    }
  }
}
</style>
